import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRadio from '@nxsys/components/AppRadio';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import { defaultContractDetailTypeOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import clsx from 'clsx';
import { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';

export type ContractDetailValues = {
  contractDetailType: string;
  employmentStartDate: Moment | null;
};

export type ContractDetailFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => ContractDetailValues;
  resetFields: () => void;
};

type ContractDetailFormProps = {
  formRef?: Ref<ContractDetailFormRef>;
  initialValues?: ContractDetailValues;
};

const ContractDetailForm: React.FC<ContractDetailFormProps> = ({
  formRef,
  initialValues,
}) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm<ContractDetailValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={{
        contractDetailType: defaultContractDetailTypeOptions[0].value,
        ...initialValues,
      }}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <div className="text-[18px] font-bold">
        {messages['common.contractDetail']}
      </div>
      <div className="text-[16px] text-gray-500 mb-[40px]">
        {messages['common.contractDetailHelperText']}
      </div>
      <Form.Item name="contractDetailType">
        <AppRadioGroup className="flex flex-col gap-y-[16px]">
          {defaultContractDetailTypeOptions.map((option) => (
            <div
              key={option.value}
              className={clsx(
                'border border-gray-300 rounded-[12px] p-[16px] w-[456px]',
                {
                  'border-primary-500':
                    option.value === watchValues?.contractDetailType,
                },
              )}
            >
              <AppRadio value={option.value}>
                <div className="text-[20px] font-bold leading-none mb-[8px]">
                  {option.label}
                </div>
                <div className="text-[14px] text-gray-500">
                  {option.description}
                </div>
              </AppRadio>
            </div>
          ))}
        </AppRadioGroup>
      </Form.Item>
      <Form.Item
        name="employmentStartDate"
        label={messages['common.employmentStartDate']}
        rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
      >
        <div className="text-[12px] text-gray-500 mb-2">
          {messages['common.employmentStartDateHelperText']}
        </div>
        <AppDatePicker
          format="DD/MM/YYYY"
          placeholder={messages['form.selectDate']}
          value={watchValues?.employmentStartDate}
          onChange={(value: Moment | null) => {
            form.setFieldsValue({ employmentStartDate: value });
          }}
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default ContractDetailForm;
